import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../globals/utils/api";
import {
  getDuplicateEmailAlertProps,
  isRegistrationSubmitDisabled,
  shouldClearDuplicateEmailCheck,
} from "./registrationDuplicateEmail";

/**
 * Hook used by public and invite registration screens to detect at email-blur
 * time whether the supplied address already maps to another Vermost portal account.
 *
 * On invite registration routes, the hook reads `:token` from the URL automatically.
 * You can still pass `inviteToken` explicitly when the route param name differs.
 *
 * Returns:
 *   { existingAccounts, hasDuplicate, hasPendingInvite, showPendingInviteWarning,
 *     isRegistrationBlocked, checking, checkEmail, reset, getAlertProps,
 *     isSubmitDisabled, clearIfBlocked, checkEmailOnBlur }
 */
const useDuplicateEmailCheck = (inviteToken) => {
  const { token: routeInviteToken } = useParams();
  const resolvedInviteToken = inviteToken ?? routeInviteToken;
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [hasPendingInvite, setHasPendingInvite] = useState(false);
  const [checking, setChecking] = useState(false);

  const reset = useCallback(() => {
    setExistingAccounts([]);
    setHasPendingInvite(false);
  }, []);

  const checkEmail = useCallback(
    async (email) => {
      if (!email || !email.includes("@")) {
        setExistingAccounts([]);
        setHasPendingInvite(false);
        return [];
      }
      setChecking(true);
      try {
        const body = { email: email.trim() };
        if (resolvedInviteToken) {
          body.invite_token = resolvedInviteToken;
        }
        const data = await apiClient(
          "/accounts-by-email",
          "POST",
          body,
          { include_accounts: true }
        );
        const accounts = Array.isArray(data?.accounts) ? data.accounts : [];
        setExistingAccounts(accounts);
        setHasPendingInvite(Boolean(data?.has_pending_invite));
        return accounts;
      } catch (err) {
        console.warn("accounts-by-email check failed", err);
        setExistingAccounts([]);
        setHasPendingInvite(false);
        return [];
      } finally {
        setChecking(false);
      }
    },
    [resolvedInviteToken]
  );

  const hasDuplicate = existingAccounts.length > 0;
  const onInviteRegistrationPage = Boolean(resolvedInviteToken);
  const showPendingInviteWarning = hasPendingInvite && !onInviteRegistrationPage;
  const isRegistrationBlocked = hasDuplicate || showPendingInviteWarning;

  const duplicateEmailState = {
    existingAccounts,
    showPendingInviteWarning,
    isRegistrationBlocked,
  };

  const getAlertProps = useCallback(
    (email) => getDuplicateEmailAlertProps(duplicateEmailState, email),
    [existingAccounts, showPendingInviteWarning, isRegistrationBlocked]
  );

  const isSubmitDisabled = useCallback(
    (loading, extraDisabled = false) =>
      isRegistrationSubmitDisabled(loading, duplicateEmailState, extraDisabled),
    [isRegistrationBlocked]
  );

  const clearIfBlocked = useCallback(() => {
    if (shouldClearDuplicateEmailCheck(duplicateEmailState)) {
      reset();
    }
  }, [isRegistrationBlocked, reset]);

  const checkEmailOnBlur = useCallback(
    (validateEmail, email) => {
      if (validateEmail()) {
        return checkEmail(email);
      }
      return Promise.resolve([]);
    },
    [checkEmail]
  );

  return {
    existingAccounts,
    hasDuplicate,
    hasPendingInvite,
    showPendingInviteWarning,
    isRegistrationBlocked,
    checking,
    checkEmail,
    reset,
    getAlertProps,
    isSubmitDisabled,
    clearIfBlocked,
    checkEmailOnBlur,
  };
};

export default useDuplicateEmailCheck;
